// GraphQL Mutations
import { gql, useMutation } from "@apollo/client";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { EyeOff, GripVertical, Palette } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { ServiceCategory } from "@/gql/graphql";
import { cn } from "@/lib/utils";

const UPDATE_CATEGORY_SORT_ORDER_MUTATION = gql`
  mutation UpdateCategorySortOrder($salonId: ID!, $updates: [CategorySortUpdate!]!) {
    updateCategorySortOrder(salonId: $salonId, updates: $updates)
  }
`;

interface CategorySortUpdate {
  categoryId: string;
  sortOrder: number;
}

interface SortableCategoryItemProps {
  category: ServiceCategory;
  serviceCount: number;
}

function SortableCategoryItem({ category, serviceCount }: SortableCategoryItemProps) {
  const { t } = useTranslation("services");
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: category.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 p-4 rounded-lg border bg-card transition-shadow",
        isDragging && "shadow-lg z-50",
        category.isArchived && "opacity-60"
      )}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted/50 rounded"
      >
        <GripVertical className="w-4 h-4 text-muted-foreground" />
      </div>

      <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: category.color }} />

      <div className="flex-1">
        <div className="flex items-center gap-3">
          <h4 className="font-medium text-foreground">{category.name}</h4>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {t("stats.services", { count: serviceCount })}
            </Badge>
            {!category.allowOnlineBooking && (
              <Badge variant="outline" className="text-xs flex items-center gap-1">
                <EyeOff className="w-3 h-3" />
                Private
              </Badge>
            )}
            {category.isArchived && (
              <Badge variant="destructive" className="text-xs">
                Archived
              </Badge>
            )}
          </div>
        </div>
        {category.description && (
          <p className="text-sm text-muted-foreground mt-1">{category.description}</p>
        )}
      </div>
    </div>
  );
}

interface CategoryReorderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: ServiceCategory[];
  servicesByCategory: Record<string, any[]>;
  salonId: string;
  onSuccess?: () => void;
}

export function CategoryReorderDialog({
  open,
  onOpenChange,
  categories,
  servicesByCategory,
  salonId,
  onSuccess,
}: CategoryReorderDialogProps) {
  const [sortedCategories, setSortedCategories] = useState<ServiceCategory[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Initialize sorted categories when dialog opens
  useEffect(() => {
    if (open) {
      const sorted = [...categories].sort((a, b) => a.sortOrder - b.sortOrder);
      setSortedCategories(sorted);
      setHasChanges(false);
    }
  }, [open, categories]);

  // Mutation
  const [updateSortOrder, { loading }] = useMutation(UPDATE_CATEGORY_SORT_ORDER_MUTATION, {
    onCompleted: () => {
      toast.success("Category order updated successfully");
      onOpenChange(false);
      setHasChanges(false);
      onSuccess?.();
    },
    onError: (error) => {
      toast.error(`Failed to update category order: ${error.message}`);
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSortedCategories((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);
        setHasChanges(true);
        return newItems;
      });
    }
  };

  const handleSave = async () => {
    if (!hasChanges) {
      onOpenChange(false);
      return;
    }

    try {
      const updates: CategorySortUpdate[] = sortedCategories.map((category, index) => ({
        categoryId: category.id,
        sortOrder: index, // Use index as new sort order (0, 1, 2, ...)
      }));

      await updateSortOrder({
        variables: {
          salonId,
          updates,
        },
      });
    } catch (_error) {
      // Error handled by mutation onError callback
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    setHasChanges(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] h-[80vh] p-0 gap-0">
        {/* Fixed Header */}
        <div className="px-6 py-6 border-b bg-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GripVertical className="w-5 h-5" />
              Reorder Categories
            </DialogTitle>
            <DialogDescription>
              Drag and drop categories to change their display order. Categories with lower order
              numbers appear first.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {sortedCategories.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Palette className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <p>No categories to reorder</p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sortedCategories.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {sortedCategories.map((category) => (
                    <SortableCategoryItem
                      key={category.id}
                      category={category}
                      serviceCount={servicesByCategory[category.id]?.length || 0}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Fixed Footer */}
        <div className="px-6 py-4 border-t bg-card">
          {hasChanges && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 mb-4">
              <p className="text-sm text-blue-700">
                <strong>Changes detected:</strong> The category order has been modified. Click "Save
                Changes" to apply the new order.
              </p>
            </div>
          )}

          <DialogFooter className="mt-0">
            <Button type="button" variant="outline" onClick={handleCancel} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={loading || !hasChanges}>
              {loading ? "Saving..." : hasChanges ? "Save Changes" : "No Changes"}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
